import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";

import { QUERY_KEYS } from "@/api/constants";
import { RESOURCE_QUERY_META } from "@/api/resourceQuery";
import type { PaginatedResponse, PaginationParams } from "@/types/api";
import { useAdminCurrency } from "@/utils/hooks/useAdminCurrency";

import { dinersApi } from "./dinersApi";
import type {
  AdminDinerSubType,
  DinerExtraInformation,
  DinerUpdateStatusPayload,
  IDinerInfo,
  ITopUpRequest,
  ReferralInsightMembersParams,
  ReferralInsightMembersResponse,
  ReferralInsightsSummary,
  TopUpInfoResponse,
  UpdateDinersStatusResponse,
} from "./types";

export const useGetAllDinersQuery = (
  params?: PaginationParams,
  options?: { enabled?: boolean }
) => {
  const currency = useAdminCurrency();
  return useQuery({
    queryKey: [QUERY_KEYS.diners, params, currency],
    queryFn: () => dinersApi.getAllDiners(params),
    ...options,
  });
};

export const useGetDinerByIdQuery = (id: string) => {
  const currency = useAdminCurrency();
  return useQuery<IDinerInfo>({
    queryKey: [QUERY_KEYS.diners, id, currency],
    queryFn: () => dinersApi.getDinerById(id),
    enabled: !!id,
    meta: RESOURCE_QUERY_META,
  });
};

export const useGetReferralInsightsSummaryQuery = (
  id: string,
  currency: string
) => {
  return useQuery<ReferralInsightsSummary>({
    queryKey: [QUERY_KEYS.dinerReferralInsightsSummary, id, currency],
    queryFn: () => dinersApi.getReferralInsightsSummary(id, currency),
    enabled: !!id && !!currency,
    placeholderData: keepPreviousData,
  });
};

export const useGetReferralInsightMembersQuery = (
  id: string,
  params: ReferralInsightMembersParams
) => {
  return useQuery<ReferralInsightMembersResponse>({
    queryKey: [QUERY_KEYS.dinerReferralInsightMembers, id, params],
    queryFn: () => dinersApi.getReferralInsightMembers(id, params),
    enabled: !!id,
  });
};

export const useUpdateDinersStatusMutation = () => {
  return useMutation({
    mutationFn: (data: DinerUpdateStatusPayload) => {
      return dinersApi.changeDinersStatus(
        data
      ) as Promise<UpdateDinersStatusResponse>;
    },
  });
};

export const useGetAllBrandDinersIdQuery = (
  id: string,
  params?: PaginationParams,
  enabled?: boolean
) => {
  const currency = useAdminCurrency();
  return useQuery<PaginatedResponse<AdminDinerSubType>>({
    queryKey: [QUERY_KEYS.dinerBrand, id, params, currency],
    queryFn: () => dinersApi.getAllBrandDiners(id, params),
    enabled,
  });
};

export const useGetAllBranchDinersIdQuery = (
  id: string,
  params?: PaginationParams,
  enabled?: boolean
) => {
  const currency = useAdminCurrency();
  return useQuery<PaginatedResponse<AdminDinerSubType>>({
    queryKey: [QUERY_KEYS.dinerBranch, id, params, currency],
    queryFn: () => dinersApi.getAllBranchDiners(id, params),
    enabled,
  });
};

export const usePatchDinerDetailsMutation = () => {
  return useMutation({
    mutationFn: ({
      dinerId,
      payload,
    }: {
      dinerId: string;
      payload: Partial<DinerExtraInformation>;
    }) => dinersApi.patchDinerDetails(dinerId, payload),
  });
};

export const useAssignDinerToReferralRuleMutation = () => {
  return useMutation({
    mutationFn: ({
      dinerId,
      referralRuleId,
    }: {
      dinerId: string;
      referralRuleId: string;
    }) => {
      return dinersApi.postAssignDinersToReferralRule(dinerId, referralRuleId);
    },
  });
};

export const useGetDinerTopUpByIdQuery = (id: string) => {
  return useQuery<TopUpInfoResponse>({
    queryKey: [QUERY_KEYS.dinersTopUps, id],
    queryFn: () => dinersApi.getDinerTopUpById(id),
    enabled: !!id,
  });
};

export const useRequestTopUpAdminMutation = () => {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ITopUpRequest }) => {
      return dinersApi.requestTopUp(id, data);
    },
  });
};

export const useApproveTopUpMutation = () => {
  return useMutation({
    mutationFn: ({
      topUpRequestId,
      approvedAmount,
    }: {
      topUpRequestId: string;
      approvedAmount: number;
    }) => {
      return dinersApi.approveTopUp(topUpRequestId, approvedAmount);
    },
  });
};

export const useRejectTopUpMutation = () => {
  return useMutation({
    mutationFn: ({ topUpRequestId }: { topUpRequestId: string }) => {
      return dinersApi.rejectTopUp(topUpRequestId);
    },
  });
};
